import React, { useState, useEffect, useRef } from "react";
import { apiFetch } from "../utils/api";

export function useMapLoader({ id, userId, hasJoined }) {
  const [mapMetadata, setMapMetadata] = useState(null);
  const [mapChunks, setMapChunks] = useState([]);
  const [mappings, setMappings] = useState(null);
  const [loadedRows, setLoadedRows] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [config, setConfig] = useState(null);
  const [regionData, setRegionData] = useState(null);
  const isFetchingChunk = useRef(false);

  // Fetch metadata on mount
  const fetchMapMetadata = async () => {
    try {
      const response = await apiFetch(`api/gamerooms/${id}/metadata`);
      if (!response.ok) throw new Error("Failed to fetch map metadata");
      const data = await response.json();
      setMapMetadata(data.map);
      setConfig(data.config);
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    if (id) {
      fetchMapMetadata();
    }
  }, [id]);

  // Fetch region data after metadata loads
  useEffect(() => {
    if (!id || !mapMetadata) return;
    const fetchRegions = async () => {
      try {
        const response = await apiFetch(`api/gamerooms/${id}/regions`);
        if (!response.ok) return; // Regions may not exist for older maps
        const data = await response.json();
        // Decode base64 assignmentBuffer into Uint16Array
        const binaryStr = atob(data.assignmentBuffer);
        const bytes = new Uint8Array(binaryStr.length);
        for (let i = 0; i < binaryStr.length; i++) {
          bytes[i] = binaryStr.charCodeAt(i);
        }
        const assignment = new Uint16Array(bytes.buffer);
        setRegionData({
          seeds: data.seeds,
          assignment,
          width: data.width,
          height: data.height,
          regionCount: data.regionCount,
        });
      } catch (err) {
        console.warn("Failed to load region data:", err);
      }
    };
    fetchRegions();
  }, [id, mapMetadata]);

  // Fetch map chunks sequentially
  const CHUNK_SIZE = 20;
  const fetchMapChunk = async (startRow) => {
    try {
      const endRow = startRow + CHUNK_SIZE;
      const response = await apiFetch(
        `api/gamerooms/${id}/data?startRow=${startRow}&endRow=${endRow}`
      );
      if (!response.ok) throw new Error("Failed to fetch map chunk");
      const data = await response.json();
      if (data.mappings && !mappings) {
        setMappings(data.mappings);
      }
      return data;
    } catch (err) {
      throw err;
    }
  };

  useEffect(() => {
    const loadNextChunk = async () => {
      if (!mapMetadata || error || loadedRows >= mapMetadata.height) return;
      if (isFetchingChunk.current) return; // Already fetching a chunk, so skip.

      isFetchingChunk.current = true;
      setLoading(true);
      try {
        const nextChunk = await fetchMapChunk(loadedRows);
        if (nextChunk && nextChunk.chunk && nextChunk.chunk.length > 0) {
          setMapChunks((prev) => [
            ...prev,
            { startRow: nextChunk.startRow, chunk: nextChunk.chunk },
          ]);
          setLoadedRows(nextChunk.endRow);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
        isFetchingChunk.current = false;
      }
    };
    loadNextChunk();
  }, [mapMetadata, loadedRows, error]);

  // Create a flat grid from the loaded map chunks.
  const mapGrid = React.useMemo(() => {
    const grid = [];
    mapChunks.forEach(({ startRow, chunk }) => {
      chunk.forEach((row, rowIndex) => {
        const y = startRow + rowIndex;
        row.forEach((cell, x) => {
          grid.push({ cell, x, y });
        });
      });
    });
    return grid;
  }, [mapChunks]);

  const isMapLoaded = mapMetadata && loadedRows >= mapMetadata.height;

  return {
    mapMetadata,
    mapChunks,
    mappings,
    loadedRows,
    mapGrid,
    isMapLoaded,
    regionData,
    loading,
    error: error,
    setError,
    config,
  };
}
